import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { createDeck } from '../store/slices/flashcardSlice'
import { flashcardService } from '../services/flashcardService'
import Button from './ui/Button'
import Card from './ui/Card'
import LoadingSpinner from './ui/LoadingSpinner'
import { Upload, CheckCircle, AlertCircle } from 'lucide-react'

interface ImportStatus {
  fileName: string
  status: 'pending' | 'processing' | 'success' | 'error'
  message: string
  deckCount?: number
  cardCount?: number
}

interface ImportFlashcardsProps {
  onComplete?: () => void
}

const ImportFlashcards: React.FC<ImportFlashcardsProps> = ({ onComplete }) => {
  const dispatch = useDispatch()
  const [isImporting, setIsImporting] = useState(false)
  const [importStatus, setImportStatus] = useState<ImportStatus[]>([])

  const jsonFiles = {
    'fen': 'Fen Bilimleri',
    'mat': 'Matematik',
    'tr': 'Türkçe',
    'ing': 'İngilizce'
  }

  const updateStatus = (fileName: string, status: Partial<ImportStatus>) => {
    setImportStatus(prev => {
      const existing = prev.find(s => s.fileName === fileName)
      if (existing) {
        return prev.map(s => s.fileName === fileName ? { ...s, ...status } : s)
      } else {
        return [...prev, { fileName, status: 'pending', message: '', ...status }]
      }
    })
  }

  const importFlashcards = async () => {
    setIsImporting(true)
    setImportStatus([])

    try {
      for (const [fileKey, subjectName] of Object.entries(jsonFiles)) {
        updateStatus(fileKey, { status: 'processing', message: 'JSON dosyası okunuyor...' })

        try {
          // JSON dosyasını fetch ile oku
          const response = await fetch(`/soru/${fileKey}.json`)
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }

          const jsonData = await response.json()
          const data = jsonData.flashcards || jsonData // flashcards property varsa onu kullan, yoksa direkt data'yı kullan
          updateStatus(fileKey, { message: `${data.length} soru bulundu, konulara göre gruplandırılıyor...` })

          // Konulara göre grupla
          const topicGroups: { [key: string]: any[] } = {}
          data.forEach((item: any) => {
            const topic = item.topic || 'Genel'
            if (!topicGroups[topic]) {
              topicGroups[topic] = []
            }
            topicGroups[topic].push(item)
          })

          let totalCards = 0
          let deckCount = 0

          // Her konu için ayrı deste oluştur
          for (const [topic, questions] of Object.entries(topicGroups)) {
            const deckName = `${subjectName} - ${topic}`
            
            updateStatus(fileKey, { 
              message: `"${deckName}" destesi oluşturuluyor... (${questions.length} kart)` 
            })

            // Kartları hazırla
            const cardsData = questions.map(q => ({
              question: q.question,
              answer: q.answer,
              difficulty: (q.difficulty || 'medium') as any,
              tags: q.tags || []
            }))
            
            // Deste ve kartları birlikte oluştur
            const deck = await flashcardService.createDeckWithCards({
              title: deckName,
              description: `${subjectName} dersi ${topic} konusu flashcard'ları (${questions.length} kart)`,
              subject: fileKey,
              topic: topic,
              difficulty: 'mixed' as any,
              isPublic: true,
              tags: [fileKey, topic.toLowerCase().replace(/\s+/g, '_')]
            }, cardsData)
            
            deckCount++
            totalCards += cardsData.length

            // Kısa bekleme (rate limiting için)
            await new Promise(resolve => setTimeout(resolve, 200))
          }

          updateStatus(fileKey, {
            status: 'success',
            message: `Başarıyla tamamlandı!`,
            deckCount,
            cardCount: totalCards
          })

        } catch (error) {
          console.error(`${fileKey} dosyası işlenirken hata:`, error)
          updateStatus(fileKey, {
            status: 'error',
            message: `Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
          })
        }
      }

    } catch (error) {
      console.error('Import işlemi sırasında genel hata:', error)
    } finally {
      setIsImporting(false)
      // onComplete sadece başarılı durumda çağrılacak
    }
  }

  const getStatusIcon = (status: ImportStatus['status']) => {
    switch (status) {
      case 'processing':
        return <LoadingSpinner size="sm" />
      case 'success':
        return <CheckCircle className="text-green-500" size={20} />
      case 'error':
        return <AlertCircle className="text-red-500" size={20} />
      default:
        return null
    }
  }

  const getStatusColor = (status: ImportStatus['status']) => {
    switch (status) {
      case 'processing':
        return 'text-blue-600'
      case 'success':
        return 'text-green-600'
      case 'error':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Flashcard Destelerini İçe Aktar
        </h2>
        <p className="text-gray-600">
          /soru klasöründeki JSON dosyalarından flashcard destelerini oluşturun.
        </p>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">İçe Aktarılacak Dosyalar:</h3>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(jsonFiles).map(([key, name]) => (
            <div key={key} className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm">
                <div className="font-medium">{name}</div>
                <div className="text-gray-500">{key}.json</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <Button
          onClick={importFlashcards}
          disabled={isImporting}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
        >
          {isImporting ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              İçe Aktarılıyor...
            </>
          ) : (
            <>
              <Upload size={16} className="mr-2" />
              Desteler Oluştur
            </>
          )}
        </Button>
      </div>

      {importStatus.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">İşlem Durumu:</h3>
          {importStatus.map((status) => (
            <div key={status.fileName} className="flex items-start p-3 bg-gray-50 rounded-lg">
              <div className="mr-3 mt-0.5">
                {getStatusIcon(status.status)}
              </div>
              <div className="flex-1">
                <div className="font-medium">{jsonFiles[status.fileName as keyof typeof jsonFiles]}</div>
                <div className={`text-sm ${getStatusColor(status.status)}`}>
                  {status.message}
                </div>
                {status.status === 'success' && status.deckCount && status.cardCount && (
                  <div className="text-sm text-gray-600 mt-1">
                    {status.deckCount} deste, {status.cardCount} kart oluşturuldu
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

export default ImportFlashcards